import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Box, ButtonBase, Typography, useMediaQuery } from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import {
    MAP_VOTE_DEFAULT_DURATION_SECONDS,
    MAP_VOTE_LOADING_EYEBROW,
    MAP_VOTE_LOADING_HINT,
    MAP_VOTE_LOADING_TITLE,
    MAP_VOTE_RESOLVED_HOLD_MS,
    MAP_VOTE_STATUS_EYEBROW,
    MAP_VOTE_STATUS_SUBTITLE,
    MAP_VOTE_STATUS_TITLE,
    mapVoteRingProgress,
} from "../../constants/mapVote";
import MatchSearchLoader from "../common/MatchSearchLoader";
import utils from "../../universal/utils";
import { MOBILE_NAV_MQ, SHORT_VIEWPORT_MQ } from "./lobbyPageStyles";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const MotionBox = motion(Box);

const RESOLVE_GRACE_MS = 20000;

const RING_SIZE = 52;
const RING_RADIUS = 21;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const MONO = "'JetBrains Mono', monospace";
const DISPLAY = "'Orbitron', sans-serif";

const MAP_TINTS = {
    de_inferno: "rgba(148, 72, 28, 0.42)",
    de_mirage: "rgba(176, 132, 48, 0.34)",
    de_vertigo: "rgba(48, 78, 128, 0.4)",
    de_nuke: "rgba(28, 92, 92, 0.4)",
    de_ancient: "rgba(36, 92, 48, 0.4)",
    de_anubis: "rgba(156, 112, 36, 0.36)",
    de_overpass: "rgba(52, 88, 120, 0.38)",
    de_dust2: "rgba(168, 124, 44, 0.36)",
    de_dust_ii: "rgba(168, 124, 44, 0.36)",
    de_train: "rgba(64, 80, 96, 0.4)",
    de_cache: "rgba(72, 96, 56, 0.36)",
};

function remainingSeconds(deadline, durationSeconds) {
    if (deadline != null && Number.isFinite(Number(deadline))) {
        return Math.max(0, Math.ceil((Number(deadline) - Date.now()) / 1000));
    }
    const fallback = Number(durationSeconds);
    return Number.isFinite(fallback) ? Math.max(0, Math.ceil(fallback)) : 0;
}

function voterInitial(nickname) {
    const trimmed = String(nickname || "").trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

function resolvedVoteLabel({ winnerId, winnerMeta, wasTie, tieBrokenBy, topCount }) {
    const name = winnerMeta?.display || winnerId;
    if (!winnerId) return "";

    const count = Number(topCount);
    const scorePrefix =
        wasTie && Number.isFinite(count) && count > 0
            ? `Tied ${count}–${count} — `
            : wasTie
              ? "Tied — "
              : "";

    if (wasTie && tieBrokenBy === "pre_selected") {
        return `${scorePrefix}${name} wins, the map you accepted`;
    }
    if (wasTie && tieBrokenBy === "seeded_random") {
        return `${scorePrefix}${name} won the coin flip`;
    }
    return `${name} won the vote`;
}

function pendingVoteLabel(pendingVoters, viewerId) {
    const pendingCount = pendingVoters.length;
    if (pendingCount <= 0) return "Everyone has picked";

    const viewerPending = pendingVoters.some(
        (voter) => viewerId != null && String(voter.userId) === String(viewerId)
    );
    const others = viewerPending ? pendingCount - 1 : pendingCount;

    if (viewerPending && others <= 0) return "Waiting on you";
    if (viewerPending && others === 1) return "Waiting on you and 1 other";
    if (viewerPending) return `Waiting on you and ${others} others`;
    if (pendingCount === 1) return "Waiting on 1 player";
    return `Waiting on ${pendingCount} players`;
}

function mapVoteGridTemplate(count, isMobile) {
    if (count <= 0) return "1fr";
    if (isMobile) {
        return count === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))";
    }
    if (count <= 4) return `repeat(${count}, minmax(0, 1fr))`;
    if (count <= 6) return "repeat(3, minmax(0, 1fr))";
    if (count <= 8) return "repeat(4, minmax(0, 1fr))";
    return "repeat(5, minmax(0, 1fr))";
}

function mapCardMinHeight(count, isMobile) {
    if (isMobile) {
        if (count <= 2) return "8.4rem";
        if (count <= 4) return "6.4rem";
        return "5.2rem";
    }
    if (count <= 3) return "11.5rem";
    if (count === 4) return "8.6rem";
    if (count <= 6) return "8.4rem";
    return "6.6rem";
}

function splitVotersBySide(voters, { viewerId, partyUserIds }) {
    const yours = [];
    const theirs = [];
    const party = new Set((partyUserIds || []).map(String));
    if (viewerId) party.add(String(viewerId));

    const hasSides = voters.some(
        (voter) => voter?.side === "yours" || voter?.side === "theirs"
    );

    for (const voter of voters) {
        const side = hasSides
            ? voter.side === "yours"
                ? "yours"
                : "theirs"
            : party.has(String(voter.userId))
              ? "yours"
              : "theirs";
        (side === "yours" ? yours : theirs).push(voter);
    }

    yours.sort((a, b) => {
        const aSelf = viewerId != null && String(a.userId) === String(viewerId) ? 0 : 1;
        const bSelf = viewerId != null && String(b.userId) === String(viewerId) ? 0 : 1;
        return aSelf - bSelf;
    });

    return { yours, theirs };
}

function cooldownLabel(blocked) {
    const meta = getMapMetaById(blocked?.mapId);
    const name = meta?.display || blocked?.mapId;
    const streak = Number(blocked?.streak) || 0;
    const fixated = Number(blocked?.fixatedPlayers) || 0;
    const rosterSize = Number(blocked?.rosterSize) || 0;
    if (!rosterSize || !streak) return name;
    return `${name} — ${fixated} of ${rosterSize} have played it ${streak} games in a row`;
}

function MapVoteMatchLoading({ winnerName, detail }) {
    return (
        <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            role="status"
            aria-live="polite"
            sx={{
                mt: { xs: "1.25rem", xl: "1.55rem" },
                mx: "auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: { xs: "0.7rem", xl: "0.8rem" },
                [MOBILE_NAV_MQ]: {
                    mt: "1rem",
                    gap: "0.6rem",
                },
            }}
        >
            <MatchSearchLoader size="compact" diameter={36} />
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: DISPLAY,
                        fontSize: { xs: "0.58rem", xl: "0.64rem" },
                        fontWeight: 700,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.primary.main,
                        lineHeight: 1.1,
                    }}
                >
                    {MAP_VOTE_LOADING_EYEBROW}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.22rem",
                        fontSize: { xs: "0.92rem", xl: "1.02rem" },
                        fontWeight: 700,
                        color: colors.text.primary,
                        lineHeight: 1.2,
                        letterSpacing: "0.01em",
                    }}
                >
                    {winnerName
                        ? `Preparing ${winnerName}`
                        : MAP_VOTE_LOADING_TITLE}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.16rem",
                        fontSize: { xs: "0.68rem", xl: "0.74rem" },
                        fontWeight: 500,
                        color: colors.text.secondary,
                        lineHeight: 1.3,
                    }}
                >
                    {detail || MAP_VOTE_LOADING_HINT}
                </Typography>
            </Box>
        </MotionBox>
    );
}

MapVoteMatchLoading.propTypes = {
    winnerName: PropTypes.string,
    detail: PropTypes.string,
};

function CircularCountdown({ timeLeft, progress, isUrgent }) {
    const rawId = useId();
    const gradId = `mapVoteRing-${rawId.replace(/:/g, "")}`;
    const clamped = Math.min(1, Math.max(0, progress));
    const offset = RING_CIRCUMFERENCE * (1 - clamped);
    const accent = isUrgent ? colors.state.danger.main : colors.primary.main;
    const seconds = Math.max(0, timeLeft);

    return (
        <Box
            aria-label={`${seconds} seconds left to vote`}
            sx={{ flexShrink: 0 }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: { xs: "3.55rem", xl: "4.15rem" },
                    height: { xs: "3.55rem", xl: "4.15rem" },
                    [MOBILE_NAV_MQ]: {
                        width: "3.05rem",
                        height: "3.05rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        width: "2.75rem",
                        height: "2.75rem",
                    },
                }}
            >
                <Box
                    component="svg"
                    viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                    sx={{
                        width: "100%",
                        height: "100%",
                        transform: "rotate(-90deg)",
                        display: "block",
                    }}
                >
                    <defs>
                        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={colors.accent.main} />
                            <stop offset="100%" stopColor={colors.primary.main} />
                        </linearGradient>
                    </defs>
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RING_RADIUS}
                        fill="none"
                        stroke={alpha(colors.primary.main, 0.16)}
                        strokeWidth="4"
                    />
                    <circle
                        cx={RING_SIZE / 2}
                        cy={RING_SIZE / 2}
                        r={RING_RADIUS}
                        fill="none"
                        stroke={isUrgent ? accent : `url(#${gradId})`}
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={offset}
                        style={{ transition: "stroke-dashoffset 0.08s linear" }}
                    />
                </Box>
                <Typography
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        fontFamily: MONO,
                        fontSize: { xs: "0.92rem", xl: "1.08rem" },
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: isUrgent
                            ? colors.state.danger.main
                            : colors.text.primary,
                        lineHeight: 1,
                        [MOBILE_NAV_MQ]: {
                            fontSize: "0.82rem",
                        },
                    }}
                >
                    {seconds}s
                </Typography>
            </Box>
        </Box>
    );
}

CircularCountdown.propTypes = {
    timeLeft: PropTypes.number.isRequired,
    progress: PropTypes.number.isRequired,
    isUrgent: PropTypes.bool,
};

function VoteTallyMark() {
    return (
        <Box
            aria-hidden
            sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: "1.5px",
                height: "0.62rem",
                flexShrink: 0,
            }}
        >
            <Box sx={{ width: "2px", height: "38%", backgroundColor: colors.primary.main }} />
            <Box sx={{ width: "2px", height: "68%", backgroundColor: colors.primary.main }} />
            <Box sx={{ width: "2px", height: "100%", backgroundColor: colors.primary.main }} />
        </Box>
    );
}

function RosterAvatar({ voter, isSelf, size }) {
    const nickname = voter?.nickname || "Player";
    const src = voter?.avatarUrl || undefined;

    return (
        <Avatar
            alt={nickname}
            src={src}
            title={nickname}
            sx={{
                width: size,
                height: size,
                fontSize: size > 34 ? "0.78rem" : "0.68rem",
                fontWeight: 700,
                backgroundColor: alpha(colors.primary.main, 0.18),
                color: colors.primary.main,
                border: isSelf
                    ? `1.5px solid ${colors.primary.main}`
                    : "1.5px solid rgba(8,8,10,0.92)",
                boxShadow: isSelf
                    ? `0 0 0 1px ${alpha(colors.primary.main, 0.28)}`
                    : "none",
            }}
        >
            {src ? null : voterInitial(nickname)}
        </Avatar>
    );
}

RosterAvatar.propTypes = {
    voter: PropTypes.object,
    isSelf: PropTypes.bool,
    size: PropTypes.number.isRequired,
};

function AvatarCluster({ voters, viewerId, align = "right" }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: align === "left" ? "row-reverse" : "row",
                alignItems: "center",
                justifyContent: align === "left" ? "flex-start" : "flex-end",
                minWidth: 0,
            }}
        >
            {voters.map((voter, index) => (
                <Box
                    key={String(voter.userId)}
                    sx={{
                        ml: align === "right" && index > 0 ? "-0.62rem" : 0,
                        mr: align === "left" && index > 0 ? "-0.62rem" : 0,
                        position: "relative",
                        zIndex: voters.length - index,
                    }}
                >
                    <RosterAvatar
                        voter={voter}
                        isSelf={
                            viewerId != null &&
                            String(voter.userId) === String(viewerId)
                        }
                        size={36}
                    />
                </Box>
            ))}
        </Box>
    );
}

AvatarCluster.propTypes = {
    voters: PropTypes.arrayOf(PropTypes.object).isRequired,
    viewerId: PropTypes.string,
    align: PropTypes.oneOf(["left", "right"]),
};

function MapCooldownNotice({ blockedMaps }) {
    if (!Array.isArray(blockedMaps) || blockedMaps.length === 0) return null;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.45rem",
                mb: { xs: "0.7rem", xl: "0.8rem" },
                px: "0.7rem",
                py: "0.45rem",
                borderRadius: "10px",
                border: `1px solid ${colors.border.default}`,
                backgroundColor: "rgba(255,255,255,0.03)",
            }}
        >
            <LockOutlined
                sx={{
                    fontSize: "0.88rem",
                    color: colors.text.muted,
                    mt: "0.08rem",
                    flexShrink: 0,
                }}
            />
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                    }}
                >
                    Resting this match
                </Typography>
                {blockedMaps.map((blocked) => (
                    <Typography
                        key={blocked?.mapId}
                        sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            lineHeight: 1.4,
                            color: colors.text.secondary,
                        }}
                    >
                        {cooldownLabel(blocked)}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

MapCooldownNotice.propTypes = {
    blockedMaps: PropTypes.array,
};

export default function LobbyMapVoteStatus({
    matchId,
    candidates = [],
    blockedMaps = [],
    expiresAt = null,
    durationSeconds = MAP_VOTE_DEFAULT_DURATION_SECONDS,
    voters = [],
    votes = {},
    myVote = null,
    resolvedMap = null,
    wasTie = false,
    tieBrokenBy = null,
    topCount = null,
    currentUserId,
    partyUserIds = [],
    onVote,
    onDismiss,
}) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);

    const localDeadlineRef = useRef(null);
    if (localDeadlineRef.current?.matchId !== matchId) {
        const seconds = Number(durationSeconds);
        localDeadlineRef.current = {
            matchId,
            deadline:
                Date.now() +
                (Number.isFinite(seconds)
                    ? seconds
                    : MAP_VOTE_DEFAULT_DURATION_SECONDS) *
                    1000,
        };
    }
    const deadline =
        expiresAt != null && Number.isFinite(Number(expiresAt))
            ? Number(expiresAt)
            : localDeadlineRef.current.deadline;

    const [timeLeft, setTimeLeft] = useState(() =>
        remainingSeconds(deadline, durationSeconds)
    );
    const [progress, setProgress] = useState(() => {
        const remainingMs = Number.isFinite(Number(deadline))
            ? Math.max(0, Number(deadline) - Date.now())
            : remainingSeconds(deadline, durationSeconds) * 1000;
        return mapVoteRingProgress(remainingMs, durationSeconds);
    });

    const safeCandidates = useMemo(
        () =>
            Array.isArray(candidates)
                ? candidates.filter(
                      (mapId) => typeof mapId === "string" && mapId.trim()
                  )
                : [],
        [candidates]
    );

    const safeBlockedMaps = useMemo(
        () =>
            Array.isArray(blockedMaps)
                ? blockedMaps.filter(
                      (blocked) =>
                          blocked &&
                          typeof blocked.mapId === "string" &&
                          blocked.mapId.trim()
                  )
                : [],
        [blockedMaps]
    );

    const safeVoters = useMemo(
        () =>
            Array.isArray(voters)
                ? voters.filter((voter) => voter && voter.userId != null)
                : [],
        [voters]
    );

    const safeVotes = useMemo(() => {
        if (votes && typeof votes === "object" && !Array.isArray(votes)) {
            return votes;
        }
        return {};
    }, [votes]);

    const viewerId =
        currentUserId != null && currentUserId !== ""
            ? String(currentUserId)
            : null;

    const selectedMapId = useMemo(() => {
        const serverPick = viewerId
            ? (safeVotes[viewerId] ?? safeVotes[currentUserId] ?? null)
            : null;
        const localPick = typeof myVote === "string" && myVote ? myVote : null;
        const pick = resolvedMap ? serverPick : (localPick ?? serverPick);

        if (typeof pick !== "string" || !safeCandidates.includes(pick)) {
            return null;
        }
        return pick;
    }, [myVote, viewerId, currentUserId, safeVotes, safeCandidates, resolvedMap]);

    const votesByMap = useMemo(() => {
        const grouped = Object.fromEntries(
            safeCandidates.map((mapId) => [mapId, []])
        );

        for (const voter of safeVoters) {
            const userId = voter.userId;
            const isSelf = viewerId != null && String(userId) === viewerId;
            const picked = isSelf
                ? selectedMapId
                : safeVotes[userId] ?? safeVotes[String(userId)];
            if (typeof picked !== "string" || !grouped[picked]) continue;
            grouped[picked].push(voter);
        }

        return grouped;
    }, [safeCandidates, safeVoters, safeVotes, selectedMapId, viewerId]);

    const pendingVoters = useMemo(() => {
        const votedIds = new Set(
            Object.values(votesByMap).flatMap((list) =>
                list.map((voter) => String(voter.userId))
            )
        );
        return safeVoters.filter(
            (voter) => !votedIds.has(String(voter.userId))
        );
    }, [safeVoters, votesByMap]);

    const { yours, theirs } = useMemo(
        () =>
            splitVotersBySide(safeVoters, {
                viewerId,
                partyUserIds,
            }),
        [safeVoters, viewerId, partyUserIds]
    );

    const voteLocked = Boolean(resolvedMap) || timeLeft <= 0;
    const isUrgent =
        timeLeft > 0 &&
        timeLeft <=
            Math.ceil(
                (Number(durationSeconds) || MAP_VOTE_DEFAULT_DURATION_SECONDS) /
                    3
            );
    const winnerId =
        typeof resolvedMap === "string" && resolvedMap ? resolvedMap : null;
    const winnerMeta = winnerId ? getMapMetaById(winnerId) : null;
    const selectedMeta = selectedMapId ? getMapMetaById(selectedMapId) : null;
    const candidateCount = safeCandidates.length;
    const rosterCount = safeVoters.length;
    const acceptedCount = rosterCount;
    const gridTemplate = mapVoteGridTemplate(candidateCount, isMobile);
    const cardMinHeight = mapCardMinHeight(candidateCount, isMobile);
    const mapsMaxWidth =
        candidateCount <= 3
            ? "46rem"
            : candidateCount === 4
              ? "54rem"
              : candidateCount <= 6
                ? "58rem"
                : "72rem";
    const loadingDetail = winnerId
        ? resolvedVoteLabel({
              winnerId,
              winnerMeta,
              wasTie,
              tieBrokenBy,
              topCount,
          })
        : "";

    useEffect(() => {
        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const tick = () => {
            const remaining = remainingSeconds(deadline, durationSeconds);
            setTimeLeft(remaining);
            const remainingMs = Number.isFinite(Number(deadline))
                ? Math.max(0, Number(deadline) - Date.now())
                : remaining * 1000;
            setProgress(mapVoteRingProgress(remainingMs, durationSeconds));
        };

        tick();
        const id = window.setInterval(tick, reduceMotion ? 250 : 50);
        return () => window.clearInterval(id);
    }, [deadline, durationSeconds]);

    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    useEffect(() => {
        const delay = resolvedMap
            ? MAP_VOTE_RESOLVED_HOLD_MS
            : Math.max(0, deadline - Date.now()) + RESOLVE_GRACE_MS;

        const id = window.setTimeout(() => {
            if (typeof onDismissRef.current === "function") {
                onDismissRef.current();
            }
        }, delay);

        return () => window.clearTimeout(id);
    }, [resolvedMap, deadline]);

    useEffect(() => {
        const edgeBasePath = utils.getEdgeBasePath();
        const images = safeCandidates
            .map((mapId) => getMapImageUrl(mapId, edgeBasePath))
            .filter(Boolean)
            .map((url) => {
                const img = new Image();
                img.src = url;
                return img;
            });

        return () => {
            images.forEach((img) => {
                img.src = "";
            });
        };
    }, [safeCandidates]);

    const handleVote = (mapId) => {
        if (voteLocked) return;
        if (typeof onVote !== "function") return;
        onVote(mapId);
    };

    const pickLine = winnerId
        ? resolvedVoteLabel({
              winnerId,
              winnerMeta,
              wasTie,
              tieBrokenBy,
              topCount,
          })
        : selectedMapId
          ? `You picked ${selectedMeta?.display || selectedMapId}`
          : "Pick a map to lock your vote";

    return (
        <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            sx={{
                width: "100%",
                minWidth: 0,
                flex: "1 1 auto",
                minHeight: { xs: "28rem", xl: "32rem" },
                mx: 0,
                my: 0,
                boxSizing: "border-box",
                pointerEvents: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                textAlign: "left",
                position: "relative",
                overflow: "hidden",
                background: `
                    radial-gradient(
                        ellipse 70% 55% at 50% 42%,
                        ${alpha(colors.primary.main, 0.055)},
                        transparent 70%
                    )
                `,
                [MOBILE_NAV_MQ]: {
                    minHeight: 0,
                },
                [SHORT_VIEWPORT_MQ]: {
                    minHeight: 0,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "0.85rem",
                    px: { xs: "1.25rem", xl: "1.7rem" },
                    pt: { xs: "1.15rem", xl: "1.4rem" },
                    [MOBILE_NAV_MQ]: {
                        px: "0.9rem",
                        pt: "1.35rem",
                        gap: "0.55rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        px: "0.8rem",
                        pt: "1.1rem",
                    },
                }}
            >
                <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                    <Typography
                        sx={{
                            fontFamily: DISPLAY,
                            fontSize: { xs: "0.68rem", xl: "0.74rem" },
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.primary.main,
                            lineHeight: 1.1,
                            [MOBILE_NAV_MQ]: {
                                fontSize: "0.58rem",
                            },
                        }}
                    >
                        {MAP_VOTE_STATUS_EYEBROW}
                    </Typography>
                    <Typography
                        sx={{
                            mt: { xs: "0.28rem", xl: "0.32rem" },
                            fontSize: { xs: "1.2rem", xl: "1.4rem" },
                            fontWeight: 700,
                            letterSpacing: "0.01em",
                            color: colors.text.primary,
                            lineHeight: 1.15,
                            [MOBILE_NAV_MQ]: {
                                fontSize: "1.02rem",
                            },
                            [SHORT_VIEWPORT_MQ]: {
                                fontSize: "0.95rem",
                            },
                        }}
                    >
                        {MAP_VOTE_STATUS_TITLE}
                    </Typography>
                    <Typography
                        sx={{
                            mt: "0.28rem",
                            fontSize: { xs: "0.72rem", xl: "0.78rem" },
                            fontWeight: 500,
                            color: colors.text.secondary,
                            lineHeight: 1.35,
                            [MOBILE_NAV_MQ]: {
                                fontSize: "0.68rem",
                            },
                        }}
                    >
                        {MAP_VOTE_STATUS_SUBTITLE}
                    </Typography>
                </Box>
                <CircularCountdown
                    timeLeft={timeLeft}
                    progress={progress}
                    isUrgent={isUrgent}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    px: { xs: "1.25rem", xl: "1.7rem" },
                    pt: { xs: "0.85rem", xl: "1rem" },
                    [MOBILE_NAV_MQ]: {
                        px: "0.9rem",
                        pt: "0.85rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        px: "0.8rem",
                        pt: "0.65rem",
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: "0.58rem", xl: "0.62rem" },
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: colors.primary.main,
                        lineHeight: 1.2,
                    }}
                >
                    Match locked
                    <Box
                        component="span"
                        sx={{
                            color: colors.text.muted,
                            fontWeight: 600,
                        }}
                    >
                        {` · ${acceptedCount}/${Math.max(acceptedCount, 1)} accepted`}
                    </Box>
                </Typography>
                <Box
                    sx={{
                        mt: "0.55rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: { xs: "0.7rem", xl: "0.95rem" },
                        width: "100%",
                        maxWidth: "28rem",
                        [MOBILE_NAV_MQ]: {
                            gap: "0.5rem",
                        },
                    }}
                >
                    <AvatarCluster
                        voters={yours}
                        viewerId={viewerId}
                        align="right"
                    />
                    <Typography
                        sx={{
                            fontFamily: DISPLAY,
                            fontSize: { xs: "0.62rem", xl: "0.68rem" },
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            color: alpha("#fff", 0.38),
                            flexShrink: 0,
                        }}
                    >
                        VS
                    </Typography>
                    <AvatarCluster
                        voters={theirs}
                        viewerId={viewerId}
                        align="left"
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    px: { xs: "1.25rem", xl: "1.7rem" },
                    pt: { xs: "1.85rem", xl: "2.25rem" },
                    [MOBILE_NAV_MQ]: {
                        px: "0.9rem",
                        pt: "1.55rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        px: "0.8rem",
                        pt: "1.25rem",
                    },
                }}
            >
                <MapCooldownNotice blockedMaps={safeBlockedMaps} />

                {candidateCount === 0 ? (
                    <Typography
                        sx={{
                            textAlign: "center",
                            color: colors.text.secondary,
                            fontSize: "0.88rem",
                            py: "1.5rem",
                        }}
                    >
                        Waiting for map options
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: gridTemplate,
                            gap: {
                                xs: candidateCount <= 3 ? "0.7rem" : "0.5rem",
                                xl:
                                    candidateCount <= 3
                                        ? "0.85rem"
                                        : candidateCount === 4
                                          ? "0.65rem"
                                          : "0.6rem",
                            },
                            justifyContent: "center",
                            maxWidth: mapsMaxWidth,
                            width: "100%",
                            mx: "auto",
                        }}
                    >
                        {safeCandidates.map((mapId) => {
                            const meta = getMapMetaById(mapId);
                            const display = meta?.display || mapId;
                            const imageUrl = getMapImageUrl(
                                mapId,
                                utils.getEdgeBasePath()
                            );
                            const mapVoters = votesByMap[mapId] || [];
                            const isSelected = selectedMapId === mapId;
                            const isWinner = winnerId === mapId;
                            const tint = MAP_TINTS[mapId] || "rgba(20,20,24,0.35)";

                            return (
                                <ButtonBase
                                    key={mapId}
                                    disabled={voteLocked}
                                    onClick={() => handleVote(mapId)}
                                    aria-pressed={isSelected}
                                    aria-label={`Vote for ${display}`}
                                    sx={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        borderRadius: 0,
                                        overflow: "hidden",
                                        clipPath:
                                            "polygon(0 0, calc(100% - 13px) 0, 100% 13px, 100% 100%, 0 100%)",
                                        border: isWinner || isSelected
                                            ? `1px solid ${colors.primary.main}`
                                            : "1px solid rgba(255,255,255,0.07)",
                                        backgroundColor: "rgba(10,10,12,0.92)",
                                        transition:
                                            "border-color 0.15s ease-in-out, background-color 0.15s ease-in-out",
                                        "&:hover": voteLocked
                                            ? undefined
                                            : {
                                                  borderColor: colors.primary.main,
                                              },
                                        "&.Mui-disabled": {
                                            opacity: isWinner ? 1 : 0.94,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            minHeight: cardMinHeight,
                                            aspectRatio:
                                                candidateCount <= 3
                                                    ? "16 / 9"
                                                    : "16 / 10",
                                            backgroundColor: colors.background.card,
                                            backgroundImage: imageUrl
                                                ? `linear-gradient(180deg, ${tint} 0%, rgba(6,6,8,0.55) 46%, rgba(6,6,8,0.92) 100%), url(${imageUrl})`
                                                : `linear-gradient(180deg, ${tint}, rgba(8,8,10,0.95))`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                        }}
                                    >
                                        {isSelected && !winnerId ? (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    zIndex: 2,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.28rem",
                                                    px: "0.45rem",
                                                    py: "0.22rem",
                                                    backgroundColor: colors.primary.main,
                                                    color: colors.text.contrast,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.58rem",
                                                        fontWeight: 800,
                                                        letterSpacing: "0.08em",
                                                        textTransform: "uppercase",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    ✓ Your pick
                                                </Typography>
                                            </Box>
                                        ) : null}
                                        {isWinner ? (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    zIndex: 2,
                                                    px: "0.45rem",
                                                    py: "0.22rem",
                                                    backgroundColor: colors.primary.main,
                                                    color: colors.text.contrast,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.58rem",
                                                        fontWeight: 800,
                                                        letterSpacing: "0.08em",
                                                        textTransform: "uppercase",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    Winner
                                                </Typography>
                                            </Box>
                                        ) : null}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: "0.42rem",
                                                right: "0.7rem",
                                                zIndex: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.28rem",
                                                px: "0.38rem",
                                                py: "0.14rem",
                                                borderRadius: "999px",
                                                backgroundColor: "rgba(6,6,8,0.72)",
                                                border: "1px solid rgba(255,255,255,0.08)",
                                            }}
                                        >
                                            <VoteTallyMark />
                                            <Typography
                                                sx={{
                                                    fontFamily: MONO,
                                                    fontSize: "0.62rem",
                                                    fontWeight: 700,
                                                    color: colors.text.primary,
                                                    fontVariantNumeric: "tabular-nums",
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {mapVoters.length}/{Math.max(rosterCount, 1)}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                position: "absolute",
                                                left: "0.7rem",
                                                bottom: "0.55rem",
                                                zIndex: 2,
                                                fontSize: {
                                                    xs: candidateCount <= 3
                                                        ? "1.05rem"
                                                        : candidateCount === 4
                                                          ? "0.92rem"
                                                          : "0.86rem",
                                                    xl: candidateCount <= 3
                                                        ? "1.28rem"
                                                        : candidateCount === 4
                                                          ? "1.08rem"
                                                          : "0.98rem",
                                                },
                                                fontWeight: 800,
                                                letterSpacing: "0.01em",
                                                color: colors.text.primary,
                                                lineHeight: 1,
                                                textShadow: "0 4px 14px rgba(0,0,0,0.7)",
                                            }}
                                        >
                                            {display}
                                        </Typography>
                                    </Box>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                )}
                {winnerId ? (
                    <Box sx={{ maxWidth: mapsMaxWidth, width: "100%", mx: "auto" }}>
                        <MapVoteMatchLoading
                            winnerName={winnerMeta?.display || winnerId}
                            detail={loadingDetail || MAP_VOTE_LOADING_HINT}
                        />
                    </Box>
                ) : null}
            </Box>

            {winnerId ? null : (
            <Box
                sx={{
                    mt: "auto",
                    px: { xs: "1.25rem", xl: "1.7rem" },
                    pt: { xs: "0.85rem", xl: "1rem" },
                    pb: { xs: "0.35rem", xl: "0.45rem" },
                    textAlign: "center",
                    [MOBILE_NAV_MQ]: {
                        px: "0.9rem",
                        pt: "0.7rem",
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        px: "0.8rem",
                        pt: "0.55rem",
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: "0.92rem", xl: "1.02rem" },
                        fontWeight: 700,
                        color: selectedMapId
                            ? colors.text.primary
                            : colors.text.secondary,
                        lineHeight: 1.2,
                    }}
                >
                    {pickLine}
                </Typography>
                <Typography
                    aria-live="polite"
                    sx={{
                        mt: "0.28rem",
                        fontSize: { xs: "0.72rem", xl: "0.76rem" },
                        fontWeight: 600,
                        color: colors.text.muted,
                        letterSpacing: "0.02em",
                    }}
                >
                    {pendingVoteLabel(pendingVoters, viewerId)}
                </Typography>
            </Box>
            )}
        </MotionBox>
    );
}

LobbyMapVoteStatus.propTypes = {
    matchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    candidates: PropTypes.array,
    blockedMaps: PropTypes.array,
    expiresAt: PropTypes.number,
    durationSeconds: PropTypes.number,
    voters: PropTypes.array,
    votes: PropTypes.object,
    myVote: PropTypes.string,
    resolvedMap: PropTypes.string,
    wasTie: PropTypes.bool,
    tieBrokenBy: PropTypes.string,
    topCount: PropTypes.number,
    currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    partyUserIds: PropTypes.array,
    onVote: PropTypes.func,
    onDismiss: PropTypes.func,
};
